import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getWalletPortfolio,
  IPortfolioAssets,
} from "@/services/getWalletPortfolio";
import { selectUser } from "./userSlice";
import { RootState } from "../store";
import { fetchTokenMetadata } from "@/services/getTokenMetaDataBirdeye";
import {
  NATIVE_TOKEN_COMMON_ADDRESS,
  SOLANA_ADDRESS,
  SOLANA_ALTER_ADDRESS,
} from "@/utils";
import { getTokenPricesFromCG } from "@/services/getTokenPricesFromCG";
import { nativeTokenList } from "@/components/Wallet/Listing/WithdrawTokenList";
import {
  CHAIN_NATIVE_LOGO_URLS,
  CHAIN_NATIVE_TOKENS,
  ChainIdEnum,
  CombineWalletType,
  DECIMALS_NATIVE_TOKENS,
  NativeTokenByChainId,
  RPC_URLS,
  USDC_ADDRESS_CHAIN_TOKENS,
  USDT_ADDRESS_CHAIN_TOKENS,
} from "@/types/enums/WalletTypeEnum";
import { getSolBalanceByAddress } from "@/utils/solana/getSolBalanceByAddress";
import { fetchEVMBalance } from "@/utils/evm/fetchEvmBalance";
import { EXCLUDED_ADDRESSES } from "@/utils/wallet";
import { getTokenPriceInUsd } from "@/services/getTokenPriceInUsd";
import { selectCombineWallet } from "./walletSelectionSlice";
import { getUSDTPrice } from "@/services/getUsdPrices";
import { getTokenBalanceByAddress } from "@/utils/solana/getTokenBalanceByAddress";
import { fetchEvmTokenBalance } from "@/utils/evm/fetchEvmTokenBalance";
import { getTokenMetaDataBirdEyeSingle } from "@/services/getTokenMetaDataBirdeyeSingle";

export const fetchPortfolioAssets = createAsyncThunk<
  IPortfolioAssets[], // adjust to the expected return type
  {},
  { state: RootState }
>("swap/fetchPortfolioAssets", async ({}, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userData = selectUser(state);

    // 1. Fetch token metadata separately for each chain
    const portfolioResults = await Promise.all(
      userData.internalWallets.map((wallet) =>
        getWalletPortfolio({
          walletAddress: wallet.address,
          // walletAddress : "0x2639B4C2b2A353d69867645C65731bB0B54C7036",
          // chain : "base"

          // walletAddress:
          //   wallet.chain !== "solana"
          //     ? wallet.address
          //     : "6HdtKCx4Fe8YSzjromQYXuMdTgnC4xJpGcHFd3WFRXeZ",
          chain: wallet.chain.toLowerCase(),
        }).then((portfolio) => ({
          chain: wallet.chain.toLowerCase(),
          items: portfolio?.items || [],
        }))
      )
    );
    // 2. Group items by chain
    const chainGroupedItems: Record<string, any[]> = {};
    portfolioResults.forEach(({ chain, items }) => {
      if (!chainGroupedItems[chain]) {
        chainGroupedItems[chain] = [];
      }
      chainGroupedItems[chain].push(...items);
    });
    // 3. Fetch token metadata separately for each chain
    const metadataResults = await Promise.all(
      Object.entries(chainGroupedItems).map(([chain, items]) =>
        fetchTokenMetadata({
          balances: items.map((item) => ({
            ...item,
            address:
              item.address === SOLANA_ALTER_ADDRESS
                ? SOLANA_ADDRESS
                : item.address,
          })),
          chain,
        })
      )
    );
    // 4. Flatten and sort metadata results
    const combinedMetadata = metadataResults
      .flat()
      .sort((a, b) => (b.valueUsd ?? 0) - (a.valueUsd ?? 0));
    // 5️. Fetch native token balances and prices
    const cgPriceApiResponse = await getTokenPricesFromCG();
    const tokens = nativeTokenList;
    const updatedTokens = await Promise.all(
      tokens.map(async (token) => {
        const _wallet = userData.internalWallets.find(
          (wallet) => wallet.chain.toLowerCase() === token.chainId.toLowerCase()
        );
        let balance = 0;
        let uiAmount = 0;
        if (_wallet) {
          uiAmount =
            token.chainId === ChainIdEnum.solana
              ? (await getSolBalanceByAddress(_wallet.address)) ?? 0
              : Number(
                  await fetchEVMBalance(
                    _wallet.address,
                    RPC_URLS[_wallet.chain as ChainIdEnum]
                  )
                ) ?? 0;
          balance = uiAmount * 10 ** token.decimals;
        }
        const priceData = token.valueKey
          ? cgPriceApiResponse[token.valueKey] || {}
          : {};
        const priceUsd = priceData.usd || 0;
        const priceChange24h = priceData.usd_24h_change || 0;
        const valueUsd = uiAmount * priceUsd;
        const valueChangeInUsd = (valueUsd * priceChange24h) / 100;
        return {
          ...token,
          balance,
          uiAmount,
          priceUsd,
          priceChange24h,
          valueUsd,
          value: priceUsd,
          valueChangeInUsd,
        };
      })
    );

    // 6️⃣ Remove excluded assets & merge with native tokens
    const filteredAssets = combinedMetadata.filter(
      (asset) =>
        asset.address &&
        !EXCLUDED_ADDRESSES.includes(asset.address.toLowerCase())
    );

    const _tokenList = [...filteredAssets, ...updatedTokens];

    // 4️⃣ Flatten and sort metadata results
    const _combinedMetadata = _tokenList
      .flat()
      .sort((a, b) => (b.valueUsd ?? 0) - (a.valueUsd ?? 0));

    return _combinedMetadata;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

// Fetch Token Balance for USDT & USDC
async function getTokenBalances(
  tokenAddressMap: Record<ChainIdEnum, string>,
  wallet: string,
  chainId: ChainIdEnum
) {
  return chainId === ChainIdEnum.solana
    ? (await getTokenBalanceByAddress(wallet, tokenAddressMap[chainId])) ?? 0
    : Number(
        await fetchEvmTokenBalance(
          RPC_URLS[chainId],
          tokenAddressMap[chainId], // Dynamic Token Address
          wallet
        )
      ) ?? 0;
}

export const fetchSwapAssets = createAsyncThunk<
  IPortfolioAssets[], // adjust to the expected return type
  {},
  { state: RootState }
>("swap/fetchSwapAssets", async ({}, { getState, rejectWithValue }) => {
  const state = getState();
  const walletData = selectCombineWallet(state);
  const swapSelectedState = selectSwapState(state);
  const userData = selectUser(state);
  const { privyWalletList, internalWalletList, selectedType } = walletData;
  const { from, isFromBuy, buyChain, buyContractAddress } = swapSelectedState;
  if (!isFromBuy) {
    const fromTokenChain = Object.values(ChainIdEnum).includes(
      from?.token.chainId as ChainIdEnum
    )
      ? (from?.token.chainId as ChainIdEnum)
      : ChainIdEnum.solana; // Default fallback

    const _selectedWallet = (
      selectedType === CombineWalletType.SNIPER
        ? internalWalletList
        : privyWalletList
    ).find((item) => item.chain.toLowerCase() === fromTokenChain.toLowerCase());

    try {
      if (!_selectedWallet) return [];

      if (!EXCLUDED_ADDRESSES.includes(from?.token.address.toLowerCase())) {
        const balance =
          from?.token.chainId === ChainIdEnum.solana
            ? (await getSolBalanceByAddress(_selectedWallet?.address)) ?? 0
            : Number(
                await fetchEVMBalance(
                  _selectedWallet?.address,
                  RPC_URLS[fromTokenChain]
                )
              ) ?? 0;

        const tokenPrice = await getTokenPriceInUsd({
          tokenAddress:
            from?.token.chainId === ChainIdEnum.solana
              ? SOLANA_ADDRESS
              : NATIVE_TOKEN_COMMON_ADDRESS,
          chain: from.token.chainId,
        });

        const decimals = DECIMALS_NATIVE_TOKENS[fromTokenChain];
        const factor = 10 ** decimals;

        const _solanaToken = [
          {
            address:
              from?.token.chainId === ChainIdEnum.solana
                ? SOLANA_ADDRESS
                : NATIVE_TOKEN_COMMON_ADDRESS,
            decimals,
            balance: Number((balance * factor).toFixed(0)),
            uiAmount: balance,
            chainId: from?.token.chainId,
            name: CHAIN_NATIVE_TOKENS[fromTokenChain],
            symbol: CHAIN_NATIVE_TOKENS[fromTokenChain].toUpperCase(),
            icon: "https://upload.wikimedia.org/wikipedia/en/b/b9/Solana_logo.png",
            logo_uri: CHAIN_NATIVE_LOGO_URLS[fromTokenChain],
            priceUsd: tokenPrice.value,
            valueUsd: tokenPrice.value * balance,
            value: tokenPrice.value,
          },
        ];

        const find = from.tokenList.find(
          (item) => item.address.toLowerCase() === SOLANA_ADDRESS.toLowerCase()
        );
        const tokenList = find
          ? from.tokenList
          : [..._solanaToken, ...from.tokenList];

        return tokenList;
      } else {
        const tokenPrices = await getUSDTPrice();
        const usdtBalance = await getTokenBalances(
          USDT_ADDRESS_CHAIN_TOKENS,
          _selectedWallet?.address,
          fromTokenChain
        );

        const usdcBalance = await getTokenBalances(
          USDC_ADDRESS_CHAIN_TOKENS,
          _selectedWallet?.address,
          fromTokenChain
        );
        const tokenInfo = NativeTokenByChainId[fromTokenChain];
        const factorUsdt = 10 ** tokenInfo.decimals;

        return [
          {
            ...tokenInfo,
            ...tokenPrices.USDT,
            balance: Number((usdtBalance * factorUsdt).toFixed(0)),
            uiAmount: usdtBalance,
            valueUsd: tokenPrices.USDT.priceUsd * usdtBalance,
            chainId: fromTokenChain,
          },
          {
            ...tokenInfo,
            name: "USD Coin",
            symbol: "USDC",
            chainId: fromTokenChain,
            address: USDC_ADDRESS_CHAIN_TOKENS[fromTokenChain],
            icon: "https://s2.coinmarketcap.com/static/img/coins/128x128/3408.png",
            logo_uri:
              "https://s2.coinmarketcap.com/static/img/coins/128x128/3408.png",
            ...tokenPrices.USDC,
            balance: Number((usdcBalance * factorUsdt).toFixed(0)),
            uiAmount: usdcBalance,
            valueUsd: tokenPrices.USDC.priceUsd * usdcBalance,
          },
        ];
      }
    } catch (error) {
      console.error("Error fetching swap assets:", error);
      return rejectWithValue("Failed to fetch swap assets");
    }
  } else {
    const internalWallets = userData.internalWallets.map((wallet) => ({
      address: wallet.address,
      chain: wallet.chain,
      name: wallet.chain,
      wallet_client_type: wallet.chain,
      icon: "sniper_wallet",
    }));

    const _selectedWallet = internalWallets.find(
      (item) => item.chain.toLowerCase() === buyChain.toLowerCase()
    );

    if (!_selectedWallet) {
      return rejectWithValue("Wallet not selected");
    }
    const tokenMetadataResponse = await getTokenMetaDataBirdEyeSingle({
      tokenAddress: buyContractAddress,
      chain: buyChain,
    });
    const tokenPrice = await getTokenPriceInUsd({
      tokenAddress: buyContractAddress,
      chain: buyChain,
    });
    const balance =
      buyChain === ChainIdEnum.solana
        ? (await getTokenBalanceByAddress(
            _selectedWallet.address,
            buyContractAddress
          )) ?? 0
        : Number(
            await fetchEvmTokenBalance(
              RPC_URLS[buyChain as ChainIdEnum],
              buyContractAddress, // Dynamic Token Address
              _selectedWallet.address
            )
          ) ?? 0;
    const tokenInfo = {
      address: buyContractAddress,
      decimals: tokenMetadataResponse.decimals,
      // balance: 0,
      uiAmount: balance,
      balance: balance * 10 ** tokenMetadataResponse.decimals,
      chainId: buyChain,
      name: tokenMetadataResponse.name,
      symbol: tokenMetadataResponse.symbol,
      icon: tokenMetadataResponse.logo_uri,
      logo_uri: tokenMetadataResponse.logo_uri,
      priceUsd: tokenPrice.value,
      // valueUsd : 0,
      valueUsd: tokenPrice.value * balance,
      value: tokenPrice.value,
    };
    return [tokenInfo];
  }
});

export enum SWAP_KEY {
  FROM = "from",
  TO = "to",
}

const defaultPortfolioAsset: IPortfolioAssets = {
  address: "",
  decimals: 0,
  balance: 0,
  uiAmount: 0,
  chainId: "",
  name: "",
  symbol: "",
  icon: "",
  logo_uri: "",
  priceUsd: 0,
  valueUsd: 0,
  value: 0,
};

export interface ISwapItem {
  tokenList: IPortfolioAssets[];
  token: IPortfolioAssets;
  amount: number;
}

export interface ICgTokensLogos {
  address: string;
  logo: string;
}

export interface ISwapToken {
  from: ISwapItem;
  to: ISwapItem;
  estimatedFee: number;
  provider: string;
  price: number;
  priceImpact: number;
  slippage: number;
  tokens: IPortfolioAssets[];
  isLoadingTokens: boolean;
  isSwapTokenLoading: boolean;
  isFromBuy: boolean;
  buyContractAddress: string;
  buyChain: string;
  cgTokensLogos: ICgTokensLogos[];
  isOpenSwapModal: boolean;
}

const initialState: ISwapToken = {
  from: {
    tokenList: [],
    token: defaultPortfolioAsset,
    amount: 0,
  },
  to: {
    tokenList: [],
    token: defaultPortfolioAsset,
    amount: 0,
  },
  tokens: [],
  estimatedFee: 1,
  provider: "Jupiter",
  price: 0,
  priceImpact: 0,
  slippage: 3,
  isLoadingTokens: true,
  isSwapTokenLoading: false,
  isFromBuy: false,
  buyContractAddress: "",
  buyChain: "",
  cgTokensLogos: [],
  isOpenSwapModal: false,
};
export const SwapSlice = createSlice({
  name: "swap_slice",
  initialState,
  reducers: {
    setSlippage: (state, action: PayloadAction<number>) => {
      state.slippage = action.payload;
    },
    setEstimatedFee: (state, action: PayloadAction<number>) => {
      state.estimatedFee = action.payload;
    },
    setPriceImpact: (state, action: PayloadAction<number>) => {
      state.priceImpact = action.payload;
    },
    setTokenPrice: (state, action: PayloadAction<number>) => {
      state.price = action.payload;
    },
    setProvider: (state, action: PayloadAction<string>) => {
      state.provider = action.payload;
    },
    setAllTokens: (state, action: PayloadAction<IPortfolioAssets[]>) => {
      state.tokens = action.payload;
    },
    setTokenList: (
      state,
      action: PayloadAction<{ input: SWAP_KEY; tokens: IPortfolioAssets[] }>
    ) => {
      state[action.payload.input].tokenList = action.payload.tokens;
    },
    setSwapToken: (
      state,
      action: PayloadAction<{ input: SWAP_KEY; token: IPortfolioAssets }>
    ) => {
      state[action.payload.input].token = action.payload.token;
    },
    setTokenAmount: (
      state,
      action: PayloadAction<{ input: SWAP_KEY; amount: number }>
    ) => {
      state[action.payload.input].amount = action.payload.amount;
    },
    changeTokenFromto: (state) => {
      // Swap `from` and `to` values
      [state.from, state.to] = [state.to, state.from];
    },
    addCgTokenLogo: (state, action: PayloadAction<ICgTokensLogos>) => {
      // Check if the logo already exists before adding
      const existing = state.cgTokensLogos.find(
        (token) =>
          token.address.toLowerCase() === action.payload.address.toLowerCase()
      );

      if (!existing) {
        state.cgTokensLogos.push(action.payload);
      }
    },
    callBuyFromFeed: (
      state,
      action: PayloadAction<{ buyContractAddress: string; buyChain: string }>
    ) => {
      state.buyChain = action.payload.buyChain;
      state.buyContractAddress = action.payload.buyContractAddress;
      state.isFromBuy = true;
      console.log("state.tokens", state.tokens);
      console.log("state.tokens", JSON.stringify(state.tokens));
      const _filteredTokens = state.tokens.filter(
        (item) =>
          item.chainId.toLowerCase() === action.payload.buyChain.toLowerCase()
      );
      state.from.tokenList = _filteredTokens;
      //TODO-Hide from token set start
      // if (_filteredTokens.length > 0) {
      //   const [firstToken, secondToken] = _filteredTokens;
      //   const isExcluded =
      //     firstToken.address.toLowerCase() === action.payload.buyContractAddress.toLowerCase() ||
      //     EXCLUDED_ADDRESSES.includes(action.payload.buyContractAddress.toLowerCase());
      
      //   state.from.token =
      //     _filteredTokens.length > 1 && !isExcluded ? secondToken : isExcluded ? defaultPortfolioAsset : firstToken;
      // }
      //TODO-Hide from token set end

      // state.from.token = _filteredTokens.length > 0? state.tokens.filter((item) => item.chainId.toLowerCase() === action.payload.buyChain.toLowerCase())[0] ?? defaultPortfolioAsset
    },
    setIsSwapOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpenSwapModal = action.payload;
    },
    resetSwap: (state) => ({
      ...initialState,
      tokens: state.tokens, // Preserve tokens
      isLoadingTokens: state.isLoadingTokens,
      cgTokensLogos: state.cgTokensLogos,
    }), // Reset swap state to default values
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolioAssets.pending, (state) => {
        state.isLoadingTokens = true;
        console.log("Fetching Portfolio Assets Pending");
      })
      .addCase(
        fetchPortfolioAssets.fulfilled,
        (state, action: PayloadAction<IPortfolioAssets[]>) => {
          console.log("Fetching  Portfolio Assets Done", action.payload);
          state.tokens = action.payload;
          state.isLoadingTokens = false;
        }
      )
      .addCase(fetchPortfolioAssets.rejected, (state, action) => {
        state.isLoadingTokens = false;
        console.log("Fetching Portfolio Assets Error", action.payload);
      })
      .addCase(fetchSwapAssets.pending, (state) => {
        state.isSwapTokenLoading = true;
        console.log("Fetching to Assets Pending");
      })
      .addCase(
        fetchSwapAssets.fulfilled,
        (state, action: PayloadAction<IPortfolioAssets[]>) => {
          console.log("Fetching to Assets Done", action.payload);

          const _token =
            action.payload.length > 0 &&
            state.from.token.address?.toLowerCase() !==
              action.payload[0].address.toLowerCase()
              ? action.payload[0]
              : action.payload[1] ?? defaultPortfolioAsset;

          state.to.token = _token;
          state.to.tokenList = action.payload;
          state.isSwapTokenLoading = false;
        }
      )
      .addCase(fetchSwapAssets.rejected, (state, action) => {
        state.isSwapTokenLoading = false;
        console.log("Fetching to Assets Error", action.payload);
      });
  },
});

export const {
  setSlippage,
  setEstimatedFee,
  setPriceImpact,
  setTokenPrice,
  setProvider,
  setTokenList,
  setTokenAmount,
  changeTokenFromto,
  resetSwap,
  setSwapToken,
  setAllTokens,
  addCgTokenLogo,
  callBuyFromFeed,
  setIsSwapOpen,
} = SwapSlice.actions;

export const selectSwapState = (state: RootState) => state.swapState;

export default SwapSlice.reducer;
